// components/pages/Users/Users.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEnglishText } from "../../../text/useEnglishText.js";

import { getPartitionInfo } from "../../../services/userService";
import UserManageModal from "./UserManageModal";
import { useSnackbar } from "../../../components/common/AppSnackbar";
import { AuthContext } from "../../../context/AuthContext";
import { forgotPassword } from "../../../services/authService";
import AdminPageHeader from "../../../components/common/AdminPageHeader";
import EmptyState from "../../../components/common/EmptyState";
import LoadingState from "../../../components/common/LoadingState";
import { UsersRound } from "lucide-react";
import { emailVerificationStatus } from "../../../utils/emailVerificationStatus";
import "./Users.css";
const isEmptyString = (value) =>
  value === null || value === undefined || String(value).trim() === "";

function Users() {
  const { t } = useEnglishText();
  const { showSuccess } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [treeData, setTreeData] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState("add"); // add | edit | delete
  const { user } = useContext(AuthContext);

  const hasNoActiveLicense = useMemo(() => {
    const setupCompleted = user?.SETUP_COMPLETED;
    const trialEligible = user?.TRIAL_ELIGIBLE;
    const trialStartDate = user?.TRIAL_START_DATE;
    const trialEndDate = user?.TRIAL_END_DATE;
    const url = user?.URL;

    return (
      isEmptyString(setupCompleted) &&
      isEmptyString(trialEligible) &&
      isEmptyString(trialStartDate) &&
      isEmptyString(trialEndDate) &&
      isEmptyString(url)
    );
  }, [user]);

  const fetchTree = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getPartitionInfo();
      const data = res?.data || res?.DATA || res || [];
      setTreeData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch partition info:", error);
      setError(t("users.fetch_error", "Failed to load users structure"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizedTree = useMemo(() => {
    return treeData.map((company) => ({
      company_id: company.COMPANY_ID || company.company_id,
      company_name: company.COMPANY_NAME || company.company_name,
      company_abbriviation:
        company.COMPANY_ABBRIVIATION || company.company_abbriviation,
      users: Array.isArray(company.users || company.USERS || company.children)
        ? company.users || company.USERS || company.children
        : [],
    }));
  }, [treeData]);

  const handleAction = (mode, company, user = null) => {
    setModalMode(mode);
    setSelectedCompany(company);
    setSelectedUser(user);
    setOpenModal(true);
  };
  const handleChangeUserPassword = async (user) => {
    try {
      // payload مورد نیاز برای تغییر رمز عبور
      const payload = {
        sys_user_id: user.SYS_USER_ID || user.sys_user_id,
        // سایر فیلدهای مورد نیاز سرویس ریست پسورد شما
      };

      const response = await forgotPassword(payload);

      // فرض بر اینکه پاسخ سرویس شما هم ساختار استاندارد دارد
      const result = response?.data || response;

      // اگر لازم است پیامی نمایش دهید
      if (result.IsSuccess || result.success) {
        showSuccess(
          t(
            "users.messages.password_reset_success",
            "Password reset request sent.",
          ),
        );
      } else {
        throw new Error(result.ErrorMessage || "Failed to reset password");
      }
    } catch (error) {
      console.error("Change password error:", error);
      throw error; // خطا را به مدال پاس می‌دهیم تا در آنجا مدیریت شود
    }
  };
  if (hasNoActiveLicense) {
    return (
      <Box>
        <AdminPageHeader icon={UsersRound} title={t("users.title", "Users & Organization Structure")} description={t("users.description")} />
        <Box sx={{ maxWidth: 700, mx: "auto" }}>
          <EmptyState
            title={t("licenses.no_active_license_title", "No active license found")}
            description={t("licenses.no_active_license_message", "Submit a license request to continue.")}
            action={<Button variant="outlined" href="/dashboard/license-requests">{t("licenses.no_active_license_button", "Go to License Requests")}</Button>}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <AdminPageHeader icon={UsersRound} title={t("users.title", "Users & Organization Structure")} description={t("users.description")} />

      {loading ? (
        <LoadingState variant="list" label={t("common.loading", "Loading users")} />
      ) : error ? (
        <div className="admin-data-error" role="alert">{error}</div>
      ) : normalizedTree.length === 0 ? (
        <EmptyState title={t("users.no_users", "No users found")} />
      ) : (
        <section className="users-directory" aria-label={t("users.companies", "Companies")}>
          <div className="users-directory__heading">
            <div>
              <Typography component="h2" variant="h6" fontWeight={750}>
                {t("users.companies", "Companies")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("users.companies_hint", "Select a company to view and manage its users.")}
              </Typography>
            </div>
            <span data-admin-badge data-variant="secondary">
              {t("users.company_count", {
                count: normalizedTree.length,
                defaultValue: "{{count}} companies",
              })}
            </span>
          </div>

          <Stack spacing={1.5} className="users-company-list">
            {normalizedTree.map((company) => (
            <Accordion
              key={company.company_id}
              defaultExpanded={false}
              className="users-company-card"
              disableGutters
              elevation={0}
            >
              <AccordionSummary
                component="div" /* اضافه کردن این ویژگی مانع رندر شدن دکمه‌های تودرتو و بروز خطای DOM می‌شود */
                expandIcon={<ExpandMoreIcon />}
                className="users-company-card__summary"
              >
                <Box className="users-company-card__identity">
                  <span className="users-company-card__icon" aria-hidden="true">
                    <BusinessIcon />
                  </span>
                  <Box className="users-company-card__copy">
                    <Typography variant="subtitle1" fontWeight={750}>
                      {company.company_name}
                    </Typography>
                    {company.company_abbriviation && (
                      <Typography variant="caption" color="text.secondary">
                        {company.company_abbriviation}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box className="users-company-card__meta">
                  <span data-admin-badge>
                    <PersonIcon aria-hidden="true" />
                    {company.users.length === 1
                      ? t("users.user_count_single", "1 user")
                      : t("users.user_count", {
                          count: company.users.length,
                          defaultValue: "{{count}} users",
                        })}
                  </span>
                  <Typography className="users-company-card__hint" variant="caption">
                    {t("users.expand_company", "View users")}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails className="users-company-card__details">
                <Box className="users-company-card__toolbar">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={750}>{t("sidebar.users", "Users")}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("users.manage_company_users", "Manage access for this company")}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleAction("add", company)}>
                    {t("users.add_user", "Add User")}
                  </Button>
                </Box>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {company.users && company.users.length > 0 ? (
                    company.users.map((user) => (
                      <Box
                        key={user.SYS_USER_ID || user.sys_user_id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          "&:hover": {
                            backgroundColor: "var(--surface-hover)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            minWidth: 0,
                          }}
                        >
                          <PersonIcon color="action" />
                          <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            <Typography variant="body2" fontWeight="medium">
                              {user.USERNAME || user.username}
                            </Typography>
                            {(user.USER_EMAIL || user.user_email || user.EMAIL_ADDRESS) && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {user.USER_EMAIL || user.user_email || user.EMAIL_ADDRESS}
                              </Typography>
                            )}
                            <Chip
                              size="small"
                              variant="outlined"
                              color={emailVerificationStatus(user) === true ? "success" : emailVerificationStatus(user) === false ? "error" : "default"}
                              label={emailVerificationStatus(user) === true
                                ? t("dashboard.emailVerified", "Email verified")
                                : emailVerificationStatus(user) === false
                                  ? t("dashboard.emailNotVerified", "Email not verified")
                                  : t("users.emailVerificationUnknown", "Verification unknown")}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title={t("common.edit", "Edit")}>
                            <IconButton
                              size="small"
                              className="admin-icon-action"
                              aria-label={t("common.edit", "Edit")}
                              onClick={() =>
                                handleAction("edit", company, user)
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common.delete", "Delete")}>
                            <IconButton
                              size="small"
                              color="error"
                              className="admin-icon-action admin-icon-action--destructive"
                              aria-label={t("common.delete", "Delete")}
                              onClick={() =>
                                handleAction("delete", company, user)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <EmptyState title={t("users.no_users_company", "No users registered for this company.")} />
                  )}
                </Stack>
              </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </section>
      )}

      <UserManageModal
        open={openModal}
        mode={modalMode}
        company={selectedCompany}
        user={selectedUser}
        onClose={() => {
          setOpenModal(false);
          setSelectedCompany(null);
          setSelectedUser(null);
        }}
        onSuccess={fetchTree}
        onChangePassword={handleChangeUserPassword}
      />
    </Box>
  );
}

export default Users;
